import { useState } from "react";
import { PencilIcon, PencilOff, SaveIcon, Trash2Icon } from "lucide-react";

import { ConfirmationDialog, Row } from "@/components";
import { Button } from "@/components/ui/button";

import { getScreenFromPathname, mixpanelUtils } from "@/lib/mixpanel/mixpanel";

const DeleteButtonWithConfirmationDialog = ({
  onDelete,
}: {
  onDelete: () => void;
}) => {
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleConfirmDelete = () => {
    mixpanelUtils.trackButtonClick(
      "Delete resource",
      getScreenFromPathname(location.pathname),
    );
    onDelete();
    setShowConfirmation(false);
  };

  const handleDeleteClick = () => {
    setShowConfirmation(true);
  };

  return (
    <>
      <Button
        className="h-6 w-6"
        onClick={handleDeleteClick}
        size="icon"
        variant="ghost"
      >
        <Trash2Icon className="h-4 w-4 text-red-400" />
      </Button>
      <ConfirmationDialog
        confirmationButtonVariant="destructive"
        confirmButtonText="Delete"
        description="This action can't be undone."
        onConfirm={handleConfirmDelete}
        onOpenChange={setShowConfirmation}
        open={showConfirmation}
        title="Are you sure?"
      />
    </>
  );
};

export const SheetResourcesControls = ({
  disabled,
  isEditing,
  onCancel,
  onDelete,
  onEdit,
  onSave,
}: {
  disabled: boolean;
  isEditing: boolean;
  onCancel: () => void;
  onDelete: () => void;
  onEdit: () => void;
  onSave: () => void;
}) => {
  // If we are editing a record, we show the cancel and save buttons.
  if (isEditing) {
    return (
      <Row className="items-center">
        <Button
          disabled={disabled}
          onClick={() => {
            mixpanelUtils.trackButtonClick(
              "Cancel resource edit",
              getScreenFromPathname(location.pathname),
            );
            onCancel();
          }}
          size="icon"
          variant="ghost"
        >
          <PencilOff className="h-4 w-4 text-slate-600" />
        </Button>
        <Button
          disabled={disabled}
          onClick={() => {
            mixpanelUtils.trackButtonClick(
              "Save resource",
              getScreenFromPathname(location.pathname),
            );
            onSave();
          }}
          size="icon"
          variant="ghost"
        >
          <SaveIcon className="h-4 w-4 text-slate-600" />
        </Button>
      </Row>
    );
  }

  // If we are not editing a record, we show the delete and edit buttons.
  return (
    <Row className="items-center">
      <Button
        className="h-6 w-6"
        onClick={() => {
          mixpanelUtils.trackButtonClick(
            "Edit resource",
            getScreenFromPathname(location.pathname),
          );
          onEdit();
        }}
        size="icon"
        variant="ghost"
      >
        <PencilIcon className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
      </Button>
      <DeleteButtonWithConfirmationDialog onDelete={onDelete} />
    </Row>
  );
};
